import { useState } from 'react'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import {
    Alert,
    AlertTitle,
    Button,
    Checkbox,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Paper,
    Stack,
    Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate, useParams } from 'react-router'

import {
    CONTACT_VERIFICATION_EMAIL_KEY,
    CONTACT_VERIFICATION_IDENTITY_DATA_KEY,
    CONTACT_VERIFICATION_PHONE_KEY,
    CONTACT_VERIFICATION_PROCESS_STATUS_KEY,
    getItemFromLS,
    setItemToLS,
    useCompleteContactVerificationProcess,
    useContactVerificationData,
} from '../../core/api'
import { EditOutlined, InfoOutlined } from '@mui/icons-material'
import { shouldRequireOrganizationField } from './identity-helpers'

function joinParts(parts) {
    return parts.filter(Boolean).join(', ')
}

export default function VerificationReviewCard() {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { verificationToken = '' } = useParams()
    const [editDialogOpen, setEditDialogOpen] = useState(false)
    const [isAccuracyConfirmed, setIsAccuracyConfirmed] = useState(false)
    const [isProcessingAuthorized, setIsProcessingAuthorized] = useState(false)
    const [completeError, setCompleteError] = useState('')
    const { data, isPending, error } = useContactVerificationData(true)
    const completeVerification = useCompleteContactVerificationProcess()
    const identityOverride = getItemFromLS(CONTACT_VERIFICATION_IDENTITY_DATA_KEY)
    const canFinish = isAccuracyConfirmed && isProcessingAuthorized

    async function handleFinish() {
        setCompleteError('')

        try {
            const result = await completeVerification.mutateAsync({
                consentsToDataProcessing: isProcessingAuthorized,
                acknowledgesTruthfulData: isAccuracyConfirmed,
            })

            if (!result?.status) {
                throw new Error(t('verification.completeError'))
            }

            setItemToLS(CONTACT_VERIFICATION_PROCESS_STATUS_KEY, result.status)
            navigate(`/verify/${verificationToken}/status`, { replace: true })
        } catch (error) {
            setCompleteError(error?.response?.data?.message || error?.message || t('verification.completeError'))
        }
    }

    if (isPending) {
        return (
            <Container maxWidth="sm" sx={{ py: { xs: 3, md: 5 }, flexGrow: 1 }}>
                <Paper variant="outlined" sx={{ p: { xs: 3, md: 4 } }}>
                    <Stack spacing={2} alignItems="center">
                        <CircularProgress size={28} />
                        <Typography color="text.secondary">{t('verification.reviewLoading')}</Typography>
                    </Stack>
                </Paper>
            </Container>
        )
    }

    if (data?.requireProof) {
        return <Navigate to={`/verify/${verificationToken}/edit`} replace />
    }

    if (error || !data) {
        if (identityOverride) {
            const addressLine = joinParts([identityOverride.street, identityOverride.city])
            const zipCodeLine = joinParts([identityOverride.zipcode, identityOverride.country])
            const phoneLine = getItemFromLS(CONTACT_VERIFICATION_PHONE_KEY) || t('verification.notAvailable')
            const sourceLabel = t('verification.sources.manual')
            const showOrganization = shouldRequireOrganizationField(identityOverride.country, identityOverride.vat)

            return (
                <>
                    <Container maxWidth="sm" sx={{ py: { xs: 3, md: 5 }, flexGrow: 1 }}>
                        <Stack spacing={3}>
                            <Typography variant="h3">{t('verification.reviewTitle')}</Typography>
                            <Stack>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <Typography variant="h4">{t('verification.identityDataTitle')}</Typography>
                                    <Stack
                                        spacing={0.5}
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{
                                            py: 0.1,
                                            px: 0.5,
                                            fontSize: (theme) => theme.typography.caption.fontSize,
                                            borderRadius: 1,
                                            bgcolor: (theme) => theme.palette.divider,
                                            color: (theme) => theme.palette.text.secondary,
                                        }}
                                    >
                                        <InfoOutlined fontSize='inherit' />
                                        {sourceLabel.toLowerCase()}
                                    </Stack>
                                </Stack>
                                <Paper variant="outlined" sx={{ p: 3 }}>
                                    <Stack spacing={1}>
                                        <Stack>
                                            <Typography variant="caption" color="text.secondary">{t('verification.fields.vat')}</Typography>
                                            <Typography>{identityOverride.vat || t('verification.notAvailable')}</Typography>
                                        </Stack>
                                        <Stack>
                                            <Typography variant="caption" color="text.secondary">{t('verification.fields.name')}</Typography>
                                            <Typography>{identityOverride.name || t('verification.notAvailable')}</Typography>
                                        </Stack>
                                        {showOrganization && (
                                            <Stack>
                                                <Typography variant="caption" color="text.secondary">{t('verification.fields.org')}</Typography>
                                                <Typography>{identityOverride.org || t('verification.notAvailable')}</Typography>
                                            </Stack>
                                        )}
                                        {showOrganization && identityOverride.commercialRegistrationQueryCode && (
                                            <Stack>
                                                <Typography variant="caption" color="text.secondary">
                                                    {t('verification.fields.commercialRegistrationQueryCode')}
                                                </Typography>
                                                <Typography>{identityOverride.commercialRegistrationQueryCode || t('verification.notAvailable')}</Typography>
                                            </Stack>
                                        )}
                                        <Stack>
                                            <Typography variant="caption" color="text.secondary">{t('verification.fields.street')}</Typography>
                                            <Typography>{addressLine || t('verification.notAvailable')}</Typography>
                                            <Typography>{zipCodeLine || t('verification.notAvailable')}</Typography>
                                        </Stack>
                                    </Stack>
                                </Paper>
                            </Stack>

                            <Stack>
                                <Typography variant="h4">{t('verification.contactsTitle')}</Typography>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Stack spacing={2}>
                                        <Stack>
                                            <Typography variant="caption" color="text.secondary">{t('verification.emailLabel')}</Typography>
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                <Typography>{getItemFromLS(CONTACT_VERIFICATION_EMAIL_KEY) || t('verification.notAvailable')}</Typography>
                                                <CheckCircleOutlineIcon color="success" fontSize="small" />
                                            </Stack>
                                        </Stack>
                                        <Stack>
                                            <Typography variant="caption" color="text.secondary">{t('verification.phoneLabel')}</Typography>
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                <Typography>{phoneLine}</Typography>
                                                <CheckCircleOutlineIcon color="success" fontSize="small" />
                                            </Stack>
                                        </Stack>
                                    </Stack>
                                </Paper>
                            </Stack>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent={"space-between"}>
                                <Button
                                    endIcon={<EditOutlined fontSize='inherit' />}
                                    variant="outlined"
                                    onClick={() => setEditDialogOpen(true)}
                                >
                                    {t('verification.editIdentityData')}
                                </Button>
                            </Stack>
                            {completeError ? <Alert severity="error">{completeError}</Alert> : null}
                            <Stack spacing={1}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isAccuracyConfirmed}
                                            onChange={(event) => setIsAccuracyConfirmed(event.target.checked)}
                                        />
                                    }
                                    label={t('verification.confirmAccuracy')}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isProcessingAuthorized}
                                            onChange={(event) => setIsProcessingAuthorized(event.target.checked)}
                                        />
                                    }
                                    label={t('verification.authorizeProcessing')}
                                />
                            </Stack>
                            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="flex-end">
                                <Button color="primary" variant="contained" disabled={!canFinish || completeVerification.isPending} onClick={handleFinish}>{t('verification.finish')}</Button>
                            </Stack>
                        </Stack>
                    </Container>

                    <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
                        <DialogTitle>{t('verification.editIdentityData')}</DialogTitle>
                        <DialogContent>
                            <Stack spacing={1.25}>
                                <Alert severity="warning">
                                    <AlertTitle>{t('verification.manualValidationProcessTitle')}</AlertTitle>
                                    {t('verification.manualValidationDelayWarning')}
                                </Alert>
                                <Typography>{t('verification.editIdentityDataWarning')}</Typography>
                            </Stack>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setEditDialogOpen(false)}>{t('verification.close')}</Button>
                            <Button
                                onClick={() => navigate(`/verify/${verificationToken}/edit`)}
                                color="error"
                                variant="contained"
                            >
                                {t('verification.confirmEditIdentityData')}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>
            )
        }

        if (error?.response?.status === 404) {
            return <Navigate to={`/verify/${verificationToken}/create`} replace />
        }

        return (
            <Container maxWidth="sm" sx={{ py: { xs: 3, md: 5 }, flexGrow: 1 }}>
                <Paper variant="outlined" sx={{ p: { xs: 3, md: 4 } }}>
                    <Alert severity="error">{error?.response?.data?.message || error?.message || t('verification.reviewError')}</Alert>
                </Paper>
            </Container>
        )
    }

    const reviewData = identityOverride ? { ...data, ...identityOverride, source: identityOverride.source || 'manual' } : data
    const addressLine = joinParts([reviewData.street, reviewData.city])
    const zipCodeLine = joinParts([reviewData.zipcode, reviewData.country])
    const phoneLine = getItemFromLS(CONTACT_VERIFICATION_PHONE_KEY) || reviewData.phone || reviewData.mobile || t('verification.notAvailable')
    const sourceLabel = t(`verification.sources.${reviewData.source}`, { defaultValue: reviewData.source })
    const showOrganization = shouldRequireOrganizationField(reviewData.country, reviewData.vat)

    return (
        <>
            <Container maxWidth="sm" sx={{ py: { xs: 3, md: 5 }, flexGrow: 1 }}>
                <Stack spacing={3}>
                    <Typography variant="h3">{t('verification.reviewTitle')}</Typography>
                    <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography variant="h4">{t('verification.identityDataTitle')}</Typography>
                            <Stack
                                spacing={0.5}
                                direction="row"
                                alignItems="center"
                                justifyContent="center"
                                sx={{
                                    py: 0.1,
                                    px: 0.5,
                                    fontSize: (theme) => theme.typography.caption.fontSize,
                                    borderRadius: 1,
                                    bgcolor: (theme) => theme.palette.divider,
                                    color: (theme) => theme.palette.text.secondary,
                                }}
                            >
                                <InfoOutlined fontSize='inherit' />
                                {sourceLabel.toLowerCase()}
                            </Stack>
                        </Stack>
                        <Paper variant="outlined" sx={{ p: 3 }}>
                            <Stack spacing={1}>
                                <Stack>
                                    <Typography variant="caption" color="text.secondary">{t('verification.fields.vat')}</Typography>
                                    <Typography>{reviewData?.vat || t('verification.notAvailable')}</Typography>
                                </Stack>
                                <Stack>
                                    <Typography variant="caption" color="text.secondary">{t('verification.fields.name')}</Typography>
                                    <Typography>{reviewData.name || t('verification.notAvailable')}</Typography>
                                </Stack>
                                {showOrganization ? (
                                    <Stack>
                                        <Typography variant="caption" color="text.secondary">{t('verification.fields.org')}</Typography>
                                        <Typography>{reviewData.org || t('verification.notAvailable')}</Typography>
                                    </Stack>
                                ) : null}
                                {showOrganization && identityOverride?.commercialRegistrationQueryCode && (
                                    <Stack>
                                        <Typography variant="caption" color="text.secondary">
                                            {t('verification.fields.commercialRegistrationQueryCode')}
                                        </Typography>
                                        <Typography>{identityOverride.commercialRegistrationQueryCode || t('verification.notAvailable')}</Typography>
                                    </Stack>
                                )}
                                <Stack>
                                    <Typography variant="caption" color="text.secondary">{t('verification.fields.street')}</Typography>
                                    <Typography>{addressLine || t('verification.notAvailable')}</Typography>
                                    <Typography>{zipCodeLine || t('verification.notAvailable')}</Typography>
                                </Stack>
                            </Stack>
                        </Paper>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent={"space-between"}>
                            <Button
                                endIcon={<EditOutlined fontSize='inherit' />}
                                variant="outlined"
                                onClick={() => setEditDialogOpen(true)}
                            >
                                {t('verification.editIdentityData')}
                            </Button>
                        </Stack>
                    </Stack>

                    <Stack spacing={1}>
                        <Typography variant="h4">{t('verification.contactsTitle')}</Typography>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Stack spacing={2}>
                                <Stack>
                                    <Typography variant="caption" color="text.secondary">{t('verification.emailLabel')}</Typography>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Typography>{getItemFromLS(CONTACT_VERIFICATION_EMAIL_KEY) || reviewData.email || t('verification.notAvailable')}</Typography>
                                        <CheckCircleOutlineIcon color="success" fontSize="small" />
                                    </Stack>
                                </Stack>
                                <Stack>
                                    <Typography variant="caption" color="text.secondary">{t('verification.phoneLabel')}</Typography>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Typography>{phoneLine}</Typography>
                                        <CheckCircleOutlineIcon color="success" fontSize="small" />
                                    </Stack>
                                </Stack>
                            </Stack>
                        </Paper>
                    </Stack>
                    {completeError ? <Alert severity="error">{completeError}</Alert> : null}
                    <Stack spacing={1}>
                        <Stack>
                            <Alert severity="info">
                                <AlertTitle>{t('verification.reviewImportantTitle')}</AlertTitle>
                                {t('verification.reviewImportantDescription')}
                            </Alert>
                        </Stack>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isAccuracyConfirmed}
                                    onChange={(event) => setIsAccuracyConfirmed(event.target.checked)}
                                />
                            }
                            label={t('verification.confirmAccuracy')}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isProcessingAuthorized}
                                    onChange={(event) => setIsProcessingAuthorized(event.target.checked)}
                                />
                            }
                            label={t('verification.authorizeProcessing')}
                        />
                    </Stack>
                    <Button color="primary" variant="contained" disabled={!canFinish || completeVerification.isPending} onClick={handleFinish}>{t('verification.finish')}</Button>
                </Stack>
            </Container>

            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{t('verification.editIdentityData')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={1.25}>
                        <Alert severity="warning">
                            <AlertTitle>{t('verification.manualValidationProcessTitle')}</AlertTitle>
                            {t('verification.manualValidationDelayWarning')}
                        </Alert>
                        <Typography>{t('verification.editIdentityDataWarning')}</Typography>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>{t('verification.close')}</Button>
                    <Button
                        onClick={() => navigate(`/verify/${verificationToken}/edit`)}
                        color="error"
                        variant="contained"
                    >
                        {t('verification.confirmEditIdentityData')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
